import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { marketingApi } from "./client.js";
import { ErrorBanner } from "./shared.js";
import { ContentStudio } from "./workbench/ContentStudio.js";
import { PendingApprovalTable } from "./workbench/PendingApprovalTable.js";

/**
 * Workbench — the marketing operator's home.
 *
 * Layout:
 *
 *   ┌─── Marketing ───────────────  [Steer] [Mode ▾] [Pause/Resume] ┐
 *   ├─ Workbench · Schedule · Status · Knowledge · History · Settings┤
 *   │ ┌── Content studio (2/3, left) ──────┐  ┌─ Approve queue (1/3)┐ │
 *   │ │ 3 tabs: Autonomous / Ideas /        │  │ Pending approval     │ │
 *   │ │ On-demand                           │  │ table                │ │
 *   │ └─────────────────────────────────────┘  └──────────────────────┘ │
 *   └────────────────────────────────────────────────────────────────────┘
 *
 * The three ways to create content (autonomous drafting, the idea generator,
 * on-demand drafting) live in the left `ContentStudio` (2/3 width) — each one
 * lands its output in the approve queue on the right (1/3 width). Approving
 * auto-schedules the post; the **Schedule** sub-tab (its own page now) is where
 * the operator sees + adjusts the posting calendar.
 *
 * The agent's live reasoning trace ("What it's thinking") lives on the
 * **Status** sub-tab. Operator controls (pause / resume, steering, autonomy
 * mode) live in the section header so they're reachable from any marketing tab
 * — there is no in-page copy here.
 */
export function WorkbenchPage(): JSX.Element {
  // We only fetch agent-settings here to surface a top-level error banner if
  // the marketing proxy is completely unreachable. The individual panels
  // handle their own data + loading states.
  const settingsProbe = useQuery({
    queryKey: ["marketing", "agent-settings"],
    queryFn: marketingApi.getAgentSettings,
    refetchInterval: 30_000,
    retry: 0,
  });

  if (settingsProbe.isError && !settingsProbe.data) {
    return <ErrorBanner error={settingsProbe.error} />;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        alignItems: "start",
        gridTemplateColumns: { lg: "minmax(0, 2fr) minmax(0, 1fr)" },
      }}
    >
      <ContentStudio />
      <PendingApprovalTable />
    </Box>
  );
}
